/* Preserve TikTok-hosted videos and Shrine's carousel. Prefer TikTok's
 * supported player on every device; keep the existing bounded fallback. */
if (!customElements.get('easytag-tiktok-video')) {
  customElements.define('easytag-tiktok-video', class extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      const source = this.querySelector('template')?.content.querySelector('blockquote[data-video-id]');
      if (!source || !/^\d{10,25}$/.test(source.dataset.videoId)) return;
      this.videoId = source.dataset.videoId;
      this.player = this.querySelector('.easytag-tiktok-video__player');
      this.fallback = this.querySelector('.easytag-tiktok-video__fallback');
      this.link = this.querySelector('.easytag-tiktok-video__link');
      this.retry = this.querySelector('.easytag-tiktok-video__retry');
      const url = new URL(source.cite, 'https://www.tiktok.com');
      if (url.protocol !== 'https:' || !['www.tiktok.com', 'tiktok.com'].includes(url.hostname)) return;
      this.link.href = url.href;
      this.link.hidden = false;
      this.frameTitle = source.querySelector('a')?.textContent.trim() || 'TikTok';
      const caption = this.querySelector('.easytag-tiktok-video__caption');
      if (!caption.childNodes.length && source.querySelector('section')) {
        caption.append(source.querySelector('section').cloneNode(true));
        caption.querySelectorAll('a').forEach(a => a.rel = 'noopener noreferrer');
      }
      this.onMessage = event => this.receiveMessage(event);
      this.onRetry = () => this.start();
      window.addEventListener('message', this.onMessage);
      this.retry.addEventListener('click', this.onRetry);
      this.resizeObserver = new ResizeObserver(() => this.fit());
      this.resizeObserver.observe(this);
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
          if (visible && !this.dataset.state) this.start();
          else if (!visible && this.mode === 'player' && this.dataset.state === 'ready') {
            this.frame?.contentWindow.postMessage({ type: 'pause', 'x-tiktok-player': true }, 'https://www.tiktok.com');
          }
        }
      }, { rootMargin: '400px 0px', threshold: [0, 0.25] });
      this.observer.observe(this);
    }

    start() {
      this.attempted = new Set();
      // Start with the documented player, avoiding a potentially silent
      // 12-second original-embed attempt before this player can appear.
      // This is a preference, not an availability or playback guarantee.
      this.load(this.constructor.preferredMode || 'player');
    }

    load(mode) {
      clearTimeout(this.timeout);
      this.frame?.remove();
      this.attempted.add(mode);
      this.mode = mode;
      this.dataset.player = mode;
      this.dataset.state = 'loading';
      this.fallback.hidden = true;
      this.player.hidden = false;
      this.player.setAttribute('aria-busy', 'true');
      const frame = document.createElement('iframe');
      frame.name = `__tt_embed__easytag_${this.videoId}_${Math.random().toString(36).slice(2)}`;
      frame.title = `TikTok: ${this.frameTitle}`;
      frame.setAttribute('allow', 'fullscreen; picture-in-picture');
      frame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox');
      frame.tabIndex = -1;
      frame.setAttribute('aria-hidden', 'true');
      const url = new URL(`https://www.tiktok.com/${mode === 'player' ? 'player/v1' : 'embed/v2'}/${this.videoId}`);
      if (mode === 'player') {
        url.searchParams.set('autoplay', '0');
        url.searchParams.set('description', '1');
      } else {
        url.searchParams.set('lang', navigator.language || document.documentElement.lang || 'en');
        // Match the context included by TikTok's original embed loader. Do not
        // send query parameters, which can contain customer-specific data.
        url.searchParams.set('referrer', location.origin + location.pathname);
      }
      frame.src = url.href;
      frame.addEventListener('error', () => { if (this.frame === frame) this.recover(); }, { once: true });
      this.frame = frame;
      this.frameHeight = 740;
      this.player.replaceChildren(frame);
      this.fit();
      // A successful iframe load also fires for HTTP error pages. Wait for a
      // TikTok-origin initialization message; never use load as success.
      this.timeout = setTimeout(() => { if (this.frame === frame) this.recover(); }, 12000);
    }

    receiveMessage(event) {
      if (event.origin !== 'https://www.tiktok.com' || event.source !== this.frame?.contentWindow) return;
      let data = event.data;
      if (this.mode === 'player') {
        // The current TikTok player defers its documented onPlayerReady event
        // until Play is pressed. Its own boot message lets the poster/button
        // appear first; an HTTP overload page cannot send this message.
        if (data === '[tea-sdk]ready') { this.reveal(); return; }
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch { return; }
        }
        if (!data || data['x-tiktok-player'] !== true) return;
        if (data.type === 'onPlayerError') {
          // Autoplay refusal is not a failed video; keep manual Play available.
          if (Number(data.value?.errorCode) === 3002) this.reveal();
          else {
            this.constructor.preferredMode = null;
            if (Number(data.value?.errorCode) === 1001) this.fail();
            else this.recover();
          }
        } else if (['onPlayerReady', 'onStateChange', 'onCurrentTime'].includes(data.type)) {
          // Only actual playback, not a loaded poster, earns a page-local
          // preference for subsequent cards. No cookies or persistent storage.
          if ((data.type === 'onStateChange' && data.value === 1) ||
              (data.type === 'onCurrentTime' && Number(data.value?.currentTime) > 0.5)) {
            this.constructor.preferredMode = 'player';
          }
          this.reveal();
        }
        return;
      }
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (!data || data.signalSource !== this.frame.name) return;
      const height = Number(data.height);
      if (!Number.isFinite(height) || height < 300 || height > 2000) return;
      clearTimeout(this.timeout);
      this.frameHeight = height;
      this.reveal();
      this.fit();
    }

    reveal() {
      if (!this.frame || this.dataset.state === 'ready') return;
      clearTimeout(this.timeout);
      this.dataset.state = 'ready';
      this.player.setAttribute('aria-busy', 'false');
      this.frame.removeAttribute('aria-hidden');
      this.frame.removeAttribute('tabindex');
      this.fit();
    }

    fit() {
      if (!this.frame || !this.clientWidth) return;
      const width = Math.max(325, this.clientWidth);
      const scale = Math.min(1, this.clientWidth / width);
      if (this.mode === 'player') this.frameHeight = Math.round(width * 16 / 9);
      this.frame.style.width = `${width}px`;
      this.frame.style.height = `${this.frameHeight}px`;
      this.frame.style.transform = `scale(${scale})`;
      this.player.style.height = `${Math.ceil(this.frameHeight * scale)}px`;
    }

    recover() {
      // At most one automatic switch; never keep refreshing a blocked provider.
      const next = this.mode === 'embed' ? 'player' : 'embed';
      if (!this.attempted.has(next)) this.load(next);
      else this.fail();
    }

    fail() {
      clearTimeout(this.timeout);
      this.dataset.state = 'failed';
      this.frame?.remove();
      this.frame = null;
      this.player.hidden = true;
      this.player.setAttribute('aria-busy', 'false');
      this.fallback.hidden = false;
    }

    disconnectedCallback() {
      clearTimeout(this.timeout);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      window.removeEventListener('message', this.onMessage);
      this.retry?.removeEventListener('click', this.onRetry);
      this.observer = null;
      this.frame?.remove();
      this.frame = null;
      delete this.dataset.state;
      delete this.dataset.player;
    }
  });
}
